import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Flex, Heading, Image, Link, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';

const Footer = () => {
  const { t } = useTranslation();
  const apiBaseUrl = getApiBaseUrl();
  const [flags, setFlags] = useState({
    permitir_login_google: false,
    permitir_login_facebook: false,
    permitir_login_instagram: false,
    permitir_login_tiktok: false,
    chat_support_enable_whatsapp: false,
    chat_support_enable_telegram: false,
  });

  useEffect(() => {
    const toBool = (value) => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value === 1;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return ['1', 'true', 'yes', 'y', 'on'].includes(normalized);
      }
      return Boolean(value);
    };

    const loadFlags = async () => {
      try {
        const [loginResponse, chatResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/api/settings/public/login-providers`),
          fetch(`${apiBaseUrl}/api/settings/public/chat-soporte`),
        ]);

        const loginData = loginResponse.ok ? await loginResponse.json() : {};
        const chatData = chatResponse.ok ? await chatResponse.json() : {};

        setFlags({
          permitir_login_google: toBool(loginData?.permitir_login_google),
          permitir_login_facebook: toBool(loginData?.permitir_login_facebook),
          permitir_login_instagram: toBool(loginData?.permitir_login_instagram),
          permitir_login_tiktok: toBool(loginData?.permitir_login_tiktok),
          chat_support_enable_whatsapp: toBool(
            chatData?.chat_support_enable_whatsapp ?? chatData?.chat_support_whatsapp_enabled
          ),
          chat_support_enable_telegram: toBool(
            chatData?.chat_support_enable_telegram ?? chatData?.chat_support_telegram_enabled
          ),
        });
      } catch {
        setFlags({
          permitir_login_google: false,
          permitir_login_facebook: false,
          permitir_login_instagram: false,
          permitir_login_tiktok: false,
          chat_support_enable_whatsapp: false,
          chat_support_enable_telegram: false,
        });
      }
    };

    loadFlags();
  }, [apiBaseUrl]);

  const socialIcons = [
    {
      key: 'google',
      enabled: flags.permitir_login_google,
      src: '/assets/RRSS/google_color.svg',
      alt: 'Google',
    },
    {
      key: 'facebook',
      enabled: flags.permitir_login_facebook,
      src: '/assets/RRSS/facebook_color.png',
      alt: 'Facebook',
    },
    {
      key: 'instagram',
      enabled: flags.permitir_login_instagram,
      src: '/assets/RRSS/instagram_color.png',
      alt: 'Instagram',
    },
    {
      key: 'tiktok',
      enabled: flags.permitir_login_tiktok,
      src: '/assets/RRSS/tiktok_color.svg',
      alt: 'TikTok',
    },
    {
      key: 'whatsapp',
      enabled: flags.chat_support_enable_whatsapp,
      src: '/assets/RRSS/whatsapp_color.svg',
      alt: 'WhatsApp',
    },
    {
      key: 'telegram',
      enabled: flags.chat_support_enable_telegram,
      src: '/assets/RRSS/telegram_color.svg',
      alt: 'Telegram',
    },
  ].filter((item) => item.enabled);

  return (
    <Box
      as="footer"
      color="white"
      py={6}
      backgroundImage="linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1)), url('/chat_pattern.png')"
      backgroundRepeat="repeat"
      backgroundSize="auto"
      backgroundPosition="top left"
    >
      {socialIcons.length > 0 && (
        <Flex justifyContent="center" alignItems="center" gap={4} mb={5} px={4} wrap="wrap">
          {socialIcons.map((item) => (
            <Image
              key={item.key}
              src={item.src}
              alt={item.alt}
              boxSize="28px"
              objectFit="contain"
            />
          ))}
        </Flex>
      )}

      <Flex
        direction={{ base: 'column', md: 'row' }}
        maxW="1200px"
        mx="auto"
        px={4}
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex direction="column" mb={{ base: 4, md: 0 }}>
          <Heading as="h4" size="md" mb={2}>
            {t('footer_legal')}
          </Heading>
          <Link as={RouterLink} to="/aviso-legal" mb={1} _hover={{ textDecoration: 'underline' }}>
            {t('footer_aviso_legal')}
          </Link>
          <Link as={RouterLink} to="/privacidad" _hover={{ textDecoration: 'underline' }}>
            {t('footer_privacidad')}
          </Link>
        </Flex>
        <Flex direction="column" mb={{ base: 4, md: 0 }}>
          <Heading as="h4" size="md" mb={2}>
            {t('footer_soporte')}
          </Heading>
          <Link as={RouterLink} to="/faq" _hover={{ textDecoration: 'underline' }}>
            {t('footer_faq')}
          </Link>
        </Flex>
        <Text fontSize="sm" textAlign={{ base: 'center', md: 'right' }}>
          © {new Date().getFullYear()} Overtaxi.es, {t('footer_derechos')}
        </Text>
      </Flex>
    </Box>
  );
};

export default Footer;
